import {AbstractControl, AsyncValidatorFn, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {Observable, of, timeout} from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import {catchError, map, tap} from 'rxjs/operators';
import {TranslationService} from './services/translation.service';
import {IdentificationData} from "../data/models/identification-data.model";

export interface IdentificationDataFunc{
  (): IdentificationData
}

export class CustomValidators {
  private static parseBooleanLike(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) {
        return true;
      }
      if (value === 0) {
        return false;
      }
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['1', 'true', 'yes', 'y'].includes(normalized)) {
        return true;
      }
      if (['0', 'false', 'no', 'n', ''].includes(normalized)) {
        return false;
      }
      return null;
    }

    return null;
  }

  private static isEmailExistingResponse(response: any): boolean {
    const directValue = CustomValidators.parseBooleanLike(response);
    if (directValue !== null) {
      return directValue;
    }

    if (response && typeof response === 'object') {
      const candidateKeys = ['exists', 'emailExists', 'isExisting', 'result', 'value', 'data'];
      for (const key of candidateKeys) {
        const parsed = CustomValidators.parseBooleanLike(response[key]);
        if (parsed !== null) {
          return parsed;
        }
      }
    }

    return false;
  }

  static validateIBAN(http: HttpClient, translationService: TranslationService, swiftBIC: AbstractControl = null, bankName: AbstractControl = null): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      if (swiftBIC) { swiftBIC.reset(translationService.getTranslation('filled_automatically')); }
      if (bankName) { bankName.reset(translationService.getTranslation('filled_automatically')); }
      //TODO Erst prüfen wenn IBAN >= 15 ist
      return http.get<any>('/webabo/bank/iban/' + control.value).pipe(
        timeout(15000),
        // andersherum würde map nach catchError trotzdem ausgeführt! (gibt es evtl. eine pipe Function, die nur bei success greift?)
        map( (response) => {
          if (swiftBIC) { swiftBIC.patchValue(response.swiftBIC); }
          if (bankName) { bankName.patchValue(response.bankName); }
          return null;
        }),
        catchError( () => {
          return of({message: translationService.getTranslation('error_invalid_iban')});
        }),
      );
    };
  }

  static checkEmailAddress(http: HttpClient, translationService: TranslationService): AsyncValidatorFn | null {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      if (control && control.value) {
        return CustomValidators.checkIfEmailExists(control.value, http, translationService);
      } else {
        return of(null);
      }
    };
  }

  static checkIfEmailExists(email: string, http: HttpClient, translationService: TranslationService): Observable<any> {
    const httpParams = new HttpParams().append("emailAddress", email)
    return http.post<any>('/webabo/auth/check-email', httpParams).pipe(
      timeout(15000),
      // andersherum würde map nach catchError trotzdem ausgeführt! (gibt es evtl. eine pipe Function, die nur bei success greift?)
      map((response) => {
        if (CustomValidators.isEmailExistingResponse(response)) {
          throw new Error()
        } else {
          return null;
        }
      }),
      catchError(() => {
        return of({message: translationService.getTranslation('error_email_exists')});
      }),
    );
  }

  static validatePhoneNumber(httpClient: HttpClient, translationService: TranslationService, countryCode: string,
                             resultCallback?: (result: string) => void): AsyncValidatorFn {
    // Der Validator läuft bei JEDEM updateValueAndValidity() - also auch bei setValue()/patchValue()
    // mit {emitEvent: false} und bei enable() auf einer Elterngruppe. Ohne Merker wäre das je Lauf ein
    // HTTP-Request auf unverändertem Wert (s. focusout-Trim in HupSubscriptionAddressComponent).
    let validatedValues: string[] = [];
    let lastErrors: ValidationErrors | null = null;

    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = String(control?.value ?? '');
      if (value.trim() === '') {
        validatedValues = [];
        lastErrors = null;
        return of(null);
      }
      if (validatedValues.includes(value)) {
        return of(lastErrors);
      }
      return CustomValidators.validatePhone(control, countryCode, httpClient, translationService, formatted => {
        // Die formatierte Nummer schreibt die Komponente ins Control zurück; sie gilt damit als
        // geprüft, sonst löst genau dieser Rückschreiber den nächsten Request aus.
        validatedValues = [value, formatted];
        resultCallback?.(formatted);
      }).pipe(
        tap(errors => {
          if (errors?.['transportError']) {
            // Netz-/Serverfehler sind keine Aussage über die Nummer - nicht merken, sonst bleibt das
            // Feld bis zur nächsten Eingabe auf einem Fehler stehen, den ein Retry auflösen würde.
            validatedValues = [];
            lastErrors = null;
            return;
          }
          lastErrors = errors;
          if (!validatedValues.includes(value)) {
            validatedValues = [value];
          }
        })
      );
    };
  }

  static validatePhone(phone: AbstractControl, countryCode: string, httpClient: HttpClient, translationService: TranslationService, resultCallback?: (result: string) => void): Observable<ValidationErrors | null> {
    const responseBody = {
      fullNumber: phone.value.replace('/', ''),
      countryCode: countryCode
    };

    return httpClient.post<any>('/webabo/users/phone/validate', responseBody).pipe(
      map((response): ValidationErrors | null => {
        if (response && response.formattedNumber) {
          // Das formatierte Ergebnis wird an das Input übergeben
          resultCallback?.(response.formattedNumber);
          return null;
        }
        return { message: translationService.getTranslation('phone.wrongFormat') };
      }),

      catchError(() => {
        // transportError markiert einen Netz-/Serverfehler: kein Urteil über die Nummer, darf
        // deshalb im Validator nicht als geprüftes Ergebnis gemerkt werden.
        return of({ message: translationService.getTranslation('phone.validationError'), transportError: true });
      }),
    );
  }

  static checkIdentificationFieldIsCorrect(fieldIdentifier: string, data: IdentificationDataFunc): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let error = false;
      if (control.touched && control.value && control.value !== '') {
        if (fieldIdentifier === 'EMAIL' && !data().matchEmail) {
          error = true;
        }
        if (fieldIdentifier === 'PHONE' && !data().matchPhone) {
          error = true;
        }
        if (fieldIdentifier === 'BIRTHDAY' && !data().matchBirthday) {
          error = true;
        }
        if (fieldIdentifier === 'CLIENTNO' && !data().matchClientNo) {
          error = true;
        }
        if (fieldIdentifier === 'IBAN' && !data().matchIBAN) {
          error = true;
        }
        if (error) {
          // Ein synchroner Validator muss die ValidationErrors direkt liefern — ein Observable
          // landete als Fehler-Objekt {_subscribe: ...} am Control, sodass hasError('error') nie griff.
          return {error: true};
        }
      }
      return null;
    }
  }

  static checkRegisterForm(shouldValidatePassword: boolean): ValidatorFn {
    return (group: FormGroup): ValidationErrors | null => {
      const emailField = group.get('email');
      const passwordField = group.get('password');

      if(emailField.invalid) {
        return {error: true};
      }

      const passwordIsEnabled = !passwordField.disabled;
      if(shouldValidatePassword && passwordIsEnabled && (passwordField.invalid || passwordField.value.length <= 0)) {
        return {error: true};
      }

      return null;
    }
  }
}
